import type { AppData, Island, Settings, SyncState } from '../types'
import { buildSeedIslands } from '../data/seedIslands'
import { buildSeedScenarios } from '../data/seedScenarios'
import { buildLatinIslands, SEEDED_LATIN_LANGUAGES } from '../data/seedLatin'
import { buildLatinScenarios, SCENARIO_LANGUAGES } from '../data/seedLatinScenarios'
import { languageFromHost } from './languages'
import { readSessionCookie } from './session'

const KEY = 'language-island:v1'
/**
 * 6 repairs the stores that 5 stranded. 5 added B2-C2 for the four Latin
 * languages, and two more at A1. 4 added Mandarin at HSK 4-6 and two more at
 * HSK 1. 3 added the HSK 1-2 set.
 *
 * The repair is why 6 exists at all. Until the fix that ships with it,
 * reconcile() stamped the current version onto any document that passed
 * through it, so a store could be marked as having had every migration
 * without ever running one — and then no later load would try again. Those
 * stores are sitting on version 5 with the version 2 content and no way out
 * of it on their own. Bumping the number is the way out: the backfill is
 * guarded by tombstones and by id, so it adds what is missing and touches
 * nothing else.
 *
 * The bump is what makes that backfill happen exactly once. Gating it on "is
 * this island already here, and is there a tombstone for it" looked equivalent
 * and was not: tombstones are pruned after ninety days, so an island someone
 * deleted on purpose would come back three months later, and again three
 * months after that, with no way to make it stop.
 */
export const DATA_VERSION = 6

export function freshSyncState(): SyncState {
  return {
    cursor: 0,
    lastPushedAt: 0,
    lastSyncAt: null,
    token: null,
    deviceId: null,
    deviceName: null,
    accountEmail: null,
    lastError: null,
  }
}

export const DEFAULT_SETTINGS: Settings = {
  activeLanguage: 'zh',
  voiceByLanguage: {},
  voiceURI: null,
  speechRate: 0.9,
  slowRate: 0.6,
  inputMode: 'hanzi',
  requireTones: true,
  // 3 matches what the existing starter set already is, so no existing account
  // sees its library reorder itself after an update. New accounts are asked.
  hskLevel: 3,
  levelByLanguage: {},
  performSecondsPerBlock: 20,
  ambushSeconds: 15,
  showPinyinInListen: true,
  showEnglishInListen: true,
  theme: 'dark',
  reducedMotion: false,
}

export function freshData(now = Date.now()): AppData {
  return {
    version: DATA_VERSION,
    islands: [
      ...buildSeedIslands(now),
      ...SEEDED_LATIN_LANGUAGES.flatMap((code) => buildLatinIslands(code, now)),
    ],
    scenarios: [
      ...buildSeedScenarios(),
      ...SCENARIO_LANGUAGES.flatMap((code) => buildLatinScenarios(code)),
    ],
    settings: { ...DEFAULT_SETTINGS },
    history: [],
    // Default settings must not outrank settings synced from another
    // device; only a real change should win.
    settingsUpdatedAt: 0,
    tombstones: [],
    sync: freshSyncState(),
  }
}

/**
 * Fill in anything a stored payload is missing.
 *
 * Settings gain fields as the app grows, and an older save must not boot into a
 * half-configured app — so unknown-shaped input falls back field by field
 * rather than being discarded wholesale.
 *
 * @param keepSync true when reading this device's own store, false when taking
 *   data from an imported file. A backup carries the *exporting* device's
 *   cursor and token; adopting those would make this device skip every change
 *   it has not actually seen, and silently share a credential.
 */
/**
 * Add starter islands for any language the store has never seen.
 *
 * Seeding only ever happened on a first install, so an existing user gained
 * nothing when new languages shipped — their store already existed, and
 * reconcile faithfully kept exactly what was in it. This backfills, with two
 * guards: a language that already has islands is left alone, so nobody's own
 * work is joined by starters they did not ask for; and anything tombstoned
 * stays deleted, so this cannot resurrect content someone threw away.
 *
 * The same argument applies to new starter islands inside a language that
 * already exists, which the "no islands at all" guard would skip forever — so
 * the HSK 1-2 Mandarin set is backfilled by id, once, on the version bump that
 * introduced it. Doing it by id on every load instead would have resurrected
 * anything the learner deleted as soon as its tombstone was pruned.
 */
/**
 * A seed island arriving into a store that already exists.
 *
 * Seeds ship with `updatedAt: SEED_CLOCK` — zero — so that a fresh install's
 * defaults can never outrank real content synced from another device. That is
 * right on a first install and wrong here: an island backfilled into an
 * account is genuinely new to that account, and a clock of zero means
 * `collectDirty` never picks it up, so it never reaches the server and never
 * reaches the learner's other devices. Eighteen islands would appear on the
 * browser that happened to run the migration and nowhere else — which is what
 * "the islands never change, even after sync" looked like from the outside.
 */
function arriving(island: Island, now: number): Island {
  return { ...island, updatedAt: now }
}

function backfillLanguages(data: AppData, now: number, storedVersion: number): AppData {
  const deleted = new Set(data.tombstones.map((t) => t.recordId))
  const haveIslands = new Set(data.islands.map((i) => i.language))
  const haveScenarios = new Set(data.scenarios.map((s) => s.language))
  const haveIds = new Set(data.islands.map((i) => i.id))
  const islands: AppData['islands'] = []
  const scenarios: AppData['scenarios'] = []

  for (const code of SEEDED_LATIN_LANGUAGES) {
    if (haveIslands.has(code)) continue
    for (const island of buildLatinIslands(code, now)) {
      if (deleted.has(`island:${island.id}`)) continue
      islands.push(arriving(island, now))
    }
  }

  /*
   * Starter islands added, in any language, after this store was written.
   *
   * By id and across every language rather than per shipment, because there
   * have been three of these now — the HSK 1-2 identity islands at version 3,
   * HSK 4-6 at 4, B2-C2 for the four Latin languages at 5 — and the first two
   * were each written as their own named guard. A guard per shipment is a
   * guard to forget, and forgetting it means the people who most need the new
   * content are the only ones who never get it.
   *
   * Three conditions, all necessary. A store already at the current version is
   * left alone entirely, so this cannot resurrect what someone deleted once
   * its migration has run. A language the store has no islands in at all is
   * skipped — that is somebody who cleared it out, or who never used it, and
   * the loop above handles a language arriving for the first time. And a
   * tombstoned id stays deleted.
   */
  if (storedVersion < DATA_VERSION) {
    const starters = [
      ...buildSeedIslands(now),
      ...SEEDED_LATIN_LANGUAGES.flatMap((code) => buildLatinIslands(code, now)),
    ]
    for (const island of starters) {
      if (!haveIslands.has(island.language)) continue
      if (deleted.has(`island:${island.id}`) || haveIds.has(island.id)) continue
      islands.push(arriving(island, now))
    }
  }

  for (const code of SCENARIO_LANGUAGES) {
    if (haveScenarios.has(code)) continue
    for (const scenario of buildLatinScenarios(code)) {
      if (deleted.has(`scenario:${scenario.id}`)) continue
      scenarios.push(scenario)
    }
  }

  if (!islands.length && !scenarios.length) return data
  return {
    ...data,
    islands: [...data.islands, ...islands],
    scenarios: [...data.scenarios, ...scenarios],
  }
}

function reconcile(raw: unknown, keepSync: boolean): AppData {
  const fallback = freshData()
  if (!raw || typeof raw !== 'object') return fallback

  const data = raw as Partial<AppData>
  const scenarios = Array.isArray(data.scenarios) ? data.scenarios : fallback.scenarios

  return {
    /*
     * Carried through, NOT stamped to DATA_VERSION.
     *
     * The version records which migrations this document has had applied, so
     * only the code that applies them may advance it. Stamping it here marked
     * every document that merely passed through normalisation as fully
     * migrated — including an imported backup, which never sees the backfill
     * at all. The symptom is a store that says version 5 and holds the version
     * 2 content, permanently, because every later load now believes there is
     * nothing left to do.
     */
    version: Number((data as Partial<AppData>).version) || 0,
    // Everything that predates multi-language is Mandarin; stamping it here
    // means old saves and old synced records keep working untouched.
    islands: (Array.isArray(data.islands) ? data.islands : fallback.islands).map((i) => ({
      ...i,
      language: i.language ?? 'zh',
      /*
       * Unknown means theirs, not ours.
       *
       * splitByLevel only moves an island out of the way when isStarter is not
       * false, so an island arriving without the field — from an old client, a
       * hand-edited export — would be treated as shipped content and could be
       * filed above its owner. Defaulting the other way is the safe direction:
       * the worst case is a starter that never steps aside, against content
       * somebody wrote disappearing from their daily practice.
       */
      isStarter: i.isStarter ?? false,
    })),
    // Scenarios predate sync and may have no clock. Stamping them at zero means
    // the first edit on any device wins, rather than an arbitrary import time
    // beating a real change made elsewhere.
    scenarios: scenarios.map((s) => ({
      ...s,
      updatedAt: s.updatedAt ?? 0,
      language: s.language ?? 'zh',
    })),
    settings: { ...DEFAULT_SETTINGS, ...(data.settings ?? {}) },
    history: Array.isArray(data.history) ? data.history : [],
    settingsUpdatedAt: data.settingsUpdatedAt ?? 0,
    tombstones: Array.isArray(data.tombstones) ? data.tombstones : [],
    sync: keepSync ? { ...freshSyncState(), ...(data.sync ?? {}) } : freshSyncState(),
  }
}

/**
 * Take the shared session cookie when this origin has no token of its own.
 *
 * That is the normal case on a language subdomain visited for the first time:
 * the account is already signed in next door, and asking again would be five
 * logins for one account.
 */
function adoptSharedSession(data: AppData): AppData {
  if (data.sync.token) return data
  const shared = readSessionCookie()
  if (!shared) return data
  return {
    ...data,
    sync: { ...data.sync, token: shared, cursor: 0, lastPushedAt: 0, lastSyncAt: null },
  }
}

/** On a language subdomain the hostname decides, not a stored preference. */
function applyHostLanguage(data: AppData): AppData {
  const fromHost = languageFromHost()
  if (!fromHost || data.settings.activeLanguage === fromHost) return data
  return { ...data, settings: { ...data.settings, activeLanguage: fromHost } }
}

export function load(): AppData {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return applyHostLanguage(adoptSharedSession(freshData()))
    const now = Date.now()
    const reconciled = reconcile(JSON.parse(raw), true)
    // Stamped here, after the migration has actually run — see reconcile.
    const migrated = backfillLanguages(reconciled, now, reconciled.version)
    return applyHostLanguage(
      adoptSharedSession({ ...migrated, version: DATA_VERSION }),
    )
  } catch {
    // A corrupt payload should not brick the app; start clean instead.
    return applyHostLanguage(adoptSharedSession(freshData()))
  }
}

export function save(data: AppData): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(data))
  } catch {
    // Quota exhausted or storage blocked (private mode). The session keeps
    // working in memory; SettingsView surfaces export as the durable path.
  }
}

/**
 * A backup is a document, not a credential — the device token is stripped so an
 * exported file can be moved around without handing over API access.
 */
export function exportJson(data: AppData): string {
  return JSON.stringify({ ...data, sync: freshSyncState() }, null, 2)
}

export interface ImportResult {
  ok: boolean
  data?: AppData
  error?: string
}

export function importJson(text: string): ImportResult {
  try {
    const parsed = JSON.parse(text)
    if (!parsed || typeof parsed !== 'object') {
      return { ok: false, error: 'That file does not contain an object.' }
    }
    if (!Array.isArray((parsed as Partial<AppData>).islands)) {
      return { ok: false, error: 'No islands found in that file.' }
    }
    /*
     * Migrated on the way in, not just normalised. A backup taken before a
     * content release is missing whatever shipped since, and restoring it used
     * to mark the document current without ever adding that content — so the
     * restore silently cost the learner every island added since the file was
     * written.
     */
    const now = Date.now()
    const restored = reconcile(parsed, false)
    return {
      ok: true,
      data: { ...backfillLanguages(restored, now, restored.version), version: DATA_VERSION },
    }
  } catch {
    return { ok: false, error: "That file isn't valid JSON." }
  }
}

/** Suggested filename for a backup, e.g. language-island-2026-08-09.json */
export function backupFilename(now = new Date()): string {
  const iso = now.toISOString().slice(0, 10)
  return `language-island-${iso}.json`
}
